/**
 * Client-side error reporting to /api/client-error (→ pino → Loki → the
 * Grafana "Browser errors" Slack alert).
 *
 * Shared by the global listeners (components/client-error-reporter.tsx) and
 * the React error boundaries (app/error.tsx, app/global-error.tsx). Noise
 * control lives HERE, client-side, so the wire and the log stay quiet:
 * per-page-load budget, per-message dedupe, and filters for the classic
 * junk sources (opaque cross-origin "Script error.", browser extensions).
 */

const MAX_REPORTS_PER_PAGELOAD = 10;
const seen = new Set<string>();
const state = { budget: MAX_REPORTS_PER_PAGELOAD };

const EXTENSION_RE = /(?:chrome|moz|safari)-extension:\/\//;

export const reportClientError = (
  kind: string,
  message: unknown,
  stack?: unknown,
  source?: unknown,
): void => {
  if (typeof window === "undefined" || state.budget <= 0) return;
  const msg = typeof message === "string" && message.length > 0 ? message : undefined;
  if (!msg) return;
  // Opaque cross-origin errors carry no information; extension errors are not
  // our code. Both would dominate the stream if reported.
  if (msg === "Script error.") return;
  const stackStr = typeof stack === "string" ? stack : undefined;
  const sourceStr = typeof source === "string" ? source : undefined;
  if (EXTENSION_RE.test(sourceStr ?? "") || EXTENSION_RE.test(stackStr ?? "")) return;

  const key = `${kind}|${msg}`.slice(0, 300);
  if (seen.has(key)) return;
  seen.add(key);
  state.budget -= 1;

  try {
    fetch("/api/client-error", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        kind,
        message: msg.slice(0, 500),
        stack: stackStr?.slice(0, 4000),
        source: sourceStr?.slice(0, 300),
        page: window.location.pathname.slice(0, 300),
        userAgent: navigator.userAgent.slice(0, 300),
      }),
      // Survive the page unloading right after the crash (navigation away,
      // reload after a broken render).
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Reporting must never throw — an error in the error path is just lost.
  }
};
